import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import type {
  IParamsCreateSegment,
  IParamsUpdateSegment,
  IResponseSegment,
} from 'entities/Segment/api/types'
import { SegmentApi } from 'entities/Segment/api/segment'
import { Segment } from 'entities/Segment/model/Segment'

export class SegmentsStore {
  private _segmentsMap = new Map<number, Segment>()

  constructor() {
    makeAutoObservable(this)
  }

  get items() {
    return Array.from(this._segmentsMap.values())
  }

  hasItem = (id: number) => this._segmentsMap.has(id)

  getItem = (id: number) => this._segmentsMap.get(id)

  resetItems = () => {
    this._segmentsMap.clear()
  }

  createItem = async (item: IParamsCreateSegment): Promise<Segment> => {
    try {
      const { data } = await SegmentApi.createItem(item)
      const segment = new Segment(data)

      runInAction(() => {
        const currentList = [segment, ...this.items]

        this.resetItems()
        currentList.forEach((item) => {
          this._segmentsMap.set(item.id, item)
        })
      })

      return segment
    } catch (error) {
      throw error
    }
  }

  updateItem = async (id: number, item: IParamsUpdateSegment) => {
    const { data } = await SegmentApi.editItem(id, item)
    const segment = this.getItem(id)

    runInAction(() => {
      segment?.syncOrigin({ ...segment.origin, ...data })
    })

    return segment
  }

  addItem = (item: IResponseSegment) => {
    const id = item.id
    const segment = this.getItem(id)

    if (segment) {
      segment.syncOrigin({ ...segment.origin, ...item })
    } else {
      const newSegment = new Segment(item)
      this._segmentsMap.set(newSegment.id, newSegment)
    }
  }

  addItems = (items: IResponseSegment[]) => {
    items.forEach((item) => {
      this.addItem(item)
    })

    return items.reduce<Segment[]>((state, item) => {
      const segmentId = item.id
      const segment = this.getItem(segmentId)

      return segment ? [...state, segment] : [...state]
    }, [])
  }

  deleteItem = async (itemId: number) => {
    try {
      await SegmentApi.deleteItem(itemId)

      runInAction(() => {
        this._segmentsMap.delete(itemId)
      })
    } catch (error) {
      logger.error(error)
    }
  }

  fetchSegments = async () => {
    try {
      const { data } = await SegmentApi.getSegments()

      runInAction(() => {
        this.resetItems()
        this.addItems(data)
      })
    } catch (error) {
      logger.error(error)
    }
  }

  makeFavoriteItem = async (id: number) => {
    const { data } = await SegmentApi.makeFavorite(id)
    const segment = this.getItem(id)

    runInAction(() => {
      segment?.syncOrigin({ ...segment.origin, ...data })
    })

    return segment
  }

  makeUnfavoriteItem = async (id: number) => {
    const { data } = await SegmentApi.makeUnfavorite(id)
    const segment = this.getItem(id)

    runInAction(() => {
      segment?.syncOrigin({ ...segment.origin, ...data })
    })

    return segment
  }
}
